import React from 'react';
import { connect } from 'react-redux';
import { EVENTS_THRESHOLD_TYPES, INTEGRATIONS_TYPES_NAMES, VIDEO_RECORDING_PROFILE_NAMES, ACTIONS as dispatchActions, GlobalSettingsAPI, ALL_EVENT_NAMES, AUDIO_ALERT_TYPES} from './../../../data/globalsettings';
import PropTypes from 'prop-types';

import { strings } from './../../../localization';

import Audio from './Audio';
import Cameras from './Cameras';
import DailyReports from './DailyReports';
import DriverAlerts from './DriverAlerts';
import Events from './Events/Events';
import Gestures from './Gestures';
import Integrations from './Integrations/';
import UnitsOfMeasure from './UnitsOfMeasure';
import WiFi from './WiFi';
import events from 'APP_ROOT/data/events';
import { RcAPI } from '../../../components/common/rcApi';

const LOAD_ALL_SETTINGS_TIMEOUT = 25000;
export const UPDATE_SETTINGS_TIMEOUT = 15000;

const SECTION_AND_SUBSECTION_UI_STATE_REFERENCES = {
    // individual references for fine grained control, also parent section will auto-expand when a subsection reference is expanded
    // search for SUBSECTION_COLLAPSING_DISABLED to re-enable (specific steps are in the commit message)

    AUDIO: "sectionAudioExpanded",
    AUDIO_STREAMING: "subsectionStreamingVideoAudioExpanded",
    AUDIBLE_ALERTS: "sectionDriverAlertsExpanded",
    AUDIBLE_ALERTS_AUDIO_NOTIFICATIONS: "subsectionAudioNotificationsExpanded",

    CAMERAS: "sectionCamerasExpanded",
    CAMERAS_VIDEO_RECORDING_PROFILE: "subsectionVideoRecordingProfileExpanded",

    EVENTS: "sectionEventsExpanded",
    EVENTS_SPEEDING: "subsectionEventsSpeedingExpanded",
    EVENTS_AGGRESSIVE_ACCEL: "subsectionEventsAggressiveAccelExpanded",
    EVENTS_CAR_BUMPED: "subsectionEventsCarBumpedExpanded",
    EVENTS_HARD_BRAKING: "subsectionEventsHardBrakingExpanded",
    EVENTS_HARSH_CORNERING: "subsectionEventsHardCorneringExpanded",
    EVENTS_IDLING: "subsectionEventsIdlingExpanded",
    EVENTS_POSSIBLE_IMPACT: "subsectionEventsPossibleImpactExpanded",
    EVENTS_TAILGATING: "subsectionEventsTailgatingDetectionExpanded",

    REPORTS: "sectionReportsExpanded",

    GESTURES: "sectionGesturesExpanded",

    INTEGRATIONS: "sectionIntegrationsExpanded",
    INTEGRATIONS_FLEETIO: "subsectionIntegrationsFleetioCredentials",
    INTEGRATIONS_WHIPAROUND: "subsectionIntegrationsWhiparoundCredentials",

    UNIT_OF_MEASURE: "sectionUnitOfMeasureExpanded",
    UNIT_OF_MEASURE_DISTANCE: "subsectionUnitOfMeasureDistanceExpanded",
    UNIT_OF_MEASURE_TEMPERATURE: "subsectionUnitOfMeasureTemperatureExpanded",

    WIFI: "sectionWifiExpanded",
    WIFI_CREDENTIALS: "subsectionWifiHotspotsCredentialsExpanded",
    ...events.reduce((acc, event) => {
        acc[`EVENTS_${event.snakeName.toUpperCase()}`] = `subsectionEvents${event.pascalName}Expanded`;
        return acc;
    }, {})

}

export const ALL_MAIN_SECTIONS = {
    AUDIBLE_ALERTS: "AUDIBLE_ALERTS",
    AUDIO: "AUDIO",
    CAMERAS: "CAMERAS",
    EVENTS: "EVENTS",
    DAILY_REPORTS: "DAILY_REPORTS",
    GESTURES: "GESTURES",
    INTEGRATIONS: "INTEGRATIONS",
    UNIT_OF_MEASURE: "UNIT_OF_MEASURE",
    WIFI_HOTSPOT: "WIFI_HOTSPOT"
};

export const SUBSECTIONS_BY_MAIN_SECTION = {
    AUDIBLE_ALERTS: {
        ACCELEROMETER: [
            "aggressive_accel",
            "car_bumped",
            "hard_braking",
            "hard_cornering",
            "possible_impact"
        ],
        AI: [
            "camera_obscured",
            "cellphone_detection",
            "distracted_detection",
            "drinking_detection",
            "eating_detection",
            "potential_red_light_camera_violation",
            "potential_speed_camera_violation",
            "refueling",
            "smoking_detection",
            "stop_sign_violation",
            "tired_detection",
            "tailgating_detection"
        ],
        DEVICE: [
            "bad_install"
        ],
        MAP: [
            "idling",
            "speeding"
        ]
    },
    AUDIO: null,
    CAMERAS: null,
    DAILY_REPORTS: null,
    EVENTS: {
        ACCELEROMETER: [
            "aggressive_accel",
            "car_bumped",
            "hard_braking",
            "hard_cornering",
            "possible_impact"
        ],
        AI: [
            "camera_obscured",
            "cellphone_detection",
            "distracted_detection",
            "drinking_detection",
            "eating_detection",
            "potential_red_light_camera_violation",
            "potential_speed_camera_violation",
            "refueling",
            "smoking_detection",
            "stop_sign_violation",
            "tired_detection",
            "tailgating_detection"
        ],
        DEVICE: [
            "bad_install"
        ],
        MAP: [
            "idling",
            "speeding"
        ]
    },
    GESTURES: null,
    INTEGRATIONS: null,
    UNIT_OF_MEASURE: null,
    WIFI_HOTSPOT: null
};


export class GlobalSection extends React.PureComponent {

    static propTypes = {
        user: PropTypes.object.isRequired,
        accountDetails: PropTypes.object,
        features: PropTypes.object.isRequired,

        selectedSubSection: PropTypes.string,

        // Load Settings
        loadSettings: PropTypes.func.isRequired,
        timeoutLoadSettings: PropTypes.func.isRequired,
        globalSettingsPending: PropTypes.bool.isRequired,
        globalSettingsLoaded: PropTypes.bool.isRequired,
        globalSettingsErrorMessage: PropTypes.string,

        // Audio settings
        //audioExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIO,
        applyAudioAvailability: PropTypes.func.isRequired,
        timeoutAudioAvailability: PropTypes.func.isRequired,
        cameraAudioPending: PropTypes.bool.isRequired,
        cameraAudioEnabled: PropTypes.bool,
        cameraAudioErrorMessage: PropTypes.string,

        //streamingVideoAudioExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIO_STREAMING,
        applyStreamingVideoAudioAvailability: PropTypes.func.isRequired,
        timeoutStreamingVideoAudioAvailability: PropTypes.func.isRequired,
        streamingVideoAudioPending: PropTypes.bool.isRequired,
        streamingVideoAudioEnabled: PropTypes.bool,
        streamingVideoAudioErrorMessage: PropTypes.string,

        //driverAlertsExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIBLE_ALERTS,
        //audioNotificationsExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIBLE_ALERTS_AUDIO_NOTIFICATIONS,
        applyAudioNotificationsAvailability: PropTypes.func.isRequired,
        timeoutAudioNotificationsAvailability: PropTypes.func.isRequired,
        audioNotificationsPending: PropTypes.bool.isRequired,
        audioNotificationsEnabled: PropTypes.bool,
        audioNotificationsErrorMessage: PropTypes.string,

        // Cameras Settings
        //camerasExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.CAMERAS,
        applyCameraCabinAvailability: PropTypes.func.isRequired,
        timeoutCameraCabinAvailability: PropTypes.func.isRequired,
        cameraCabinPending: PropTypes.bool.isRequired,
        cameraCabinEnabled: PropTypes.bool,
        cameraCabinErrorMessage: PropTypes.string,

        applyCameraRoadAvailability: PropTypes.func.isRequired,
        timeoutCameraRoadAvailability: PropTypes.func.isRequired,
        cameraRoadPending: PropTypes.bool.isRequired,
        cameraRoadEnabled: PropTypes.bool,
        cameraRoadErrorMessage: PropTypes.string,

        //videoRecordingProfileExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.CAMERAS_VIDEO_RECORDING_PROFILE,
        applyVideoRecordingProfile: PropTypes.func.isRequired,
        timeoutVideoRecordingProfile: PropTypes.func.isRequired,
        videoRecordingProfilePending: PropTypes.bool.isRequired,
        videoRecordingProfile: PropTypes.string,
        videoRecordingProfileErrorMessage: PropTypes.string,
    
        // EventsSpeeding Settings
        //eventsExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS,
        //eventsSpeedingExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_SPEEDING,
        applyEventsSpeedingAvailability: PropTypes.func.isRequired,
        timeoutEventsSpeedingAvailability: PropTypes.func.isRequired,
        eventsSpeedingPending: PropTypes.bool.isRequired,
        eventsSpeedingEnabled: PropTypes.bool,
        eventsSpeedingErrorMessage: PropTypes.string,

        applyEventsSpeedingThreshold: PropTypes.func.isRequired,
        timeoutEventsSpeedingThreshold: PropTypes.func.isRequired,
        eventsSpeedingThresholdPending: PropTypes.bool.isRequired,
        eventsSpeedingThreshold: PropTypes.number,
        eventsSpeedingThresholdType: PropTypes.string,
        eventsSpeedingThresholdErrorMessage: PropTypes.string,

        // EventsAggressiveAccelSetttings
        //eventsAggressiveAccelExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_AGGRESSIVE_ACCEL,
        applyEventsAggressiveAccelAvailability: PropTypes.func.isRequired,
        timeoutEventsAggressiveAccelAvailability: PropTypes.func.isRequired,
        eventsAggressiveAccelPending: PropTypes.bool.isRequired,
        eventsAggressiveAccelEnabled: PropTypes.bool,
        eventsAggressiveAccelErrorMessage: PropTypes.string,

        applyEventsAggressiveAccelThreshold: PropTypes.func.isRequired,
        timeoutEventsAggressiveAccelThreshold: PropTypes.func.isRequired,
        eventsAggressiveAccelThresholdPending: PropTypes.bool.isRequired,
        eventsAggressiveAccelThreshold: PropTypes.number,
        eventsAggressiveAccelThresholdErrorMessage: PropTypes.string,

        // EventsTailgatingDetectionSettings
        // eventsTailgatingDetectionExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_TAILGATING,
        applyEventsTailgatingDetectionAvailability: PropTypes.func.isRequired,
        timeoutEventsTailgatingDetectionAvailability: PropTypes.func.isRequired,
        eventsTailgatingDetectionPending: PropTypes.bool.isRequired,
        eventsTailgatingDetectionEnabled: PropTypes.bool,
        eventsTailgatingDetectionErrorMessage: PropTypes.string,
        eventsTailgatingDetectionSpeedThreshold: PropTypes.number,
        eventsTailgatingDetectionSpeedThresholdErrorMessage: PropTypes.string,
        eventsTailgatingDetectionReactionTimeThreshold: PropTypes.number,
        eventsTailgatingDetectionReactionTimeThresholdErrorMessage: PropTypes.string,
        eventsTailgatingDetectionFollowTimeThreshold: PropTypes.number,
        eventsTailgatingDetectionFollowTimeThresholdType: PropTypes.number,
        eventsTailgatingDetectionFollowTimeThresholdErrorMessage: PropTypes.string,


        // EventsCarBumped Setttings
        //eventsCarBumpedExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_CAR_BUMPED,
        applyEventsCarBumpedAvailability: PropTypes.func.isRequired,
        timeoutEventsCarBumpedAvailability: PropTypes.func.isRequired,
        eventsCarBumpedPending: PropTypes.bool.isRequired,
        eventsCarBumpedEnabled: PropTypes.bool,
        eventsCarBumpedErrorMessage: PropTypes.string,

        applyEventsCarBumpedThreshold: PropTypes.func.isRequired,
        timeoutEventsCarBumpedThreshold: PropTypes.func.isRequired,
        eventsCarBumpedThresholdPending: PropTypes.bool.isRequired,
        eventsCarBumpedThreshold: PropTypes.number,
        eventsCarBumpedThresholdErrorMessage: PropTypes.string,


        ...events.reduce((acc, event) => {
            acc[`applyEvents${event.pascalName}Availability`] = PropTypes.func.isRequired;
            acc[`timeoutEvents${event.pascalName}Availability`] = PropTypes.func.isRequired;
            acc[`events${event.pascalName}Pending`] = PropTypes.bool.isRequired;
            acc[`events${event.pascalName}Enabled`] = PropTypes.bool;
            acc[`events${event.pascalName}ErrorMessage`] = PropTypes.string;
            return acc;
        }, {}),



        // EventsHardBraking Setttings
        //EventsHardBrakingExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_HARD_BRAKING,
        applyEventsHardBrakingAvailability: PropTypes.func.isRequired,
        timeoutEventsHardBrakingAvailability: PropTypes.func.isRequired,
        eventsHardBrakingPending: PropTypes.bool.isRequired,
        eventsHardBrakingEnabled: PropTypes.bool,
        eventsHardBrakingErrorMessage: PropTypes.string,

        applyEventsHardBrakingThreshold: PropTypes.func.isRequired,
        timeoutEventsHardBrakingThreshold: PropTypes.func.isRequired,
        eventsHardBrakingThresholdPending: PropTypes.bool.isRequired,
        eventsHardBrakingThreshold: PropTypes.number,
        eventsHardBrakingThresholdErrorMessage: PropTypes.string,

        // eventsHardCornering Setttings
        //eventsHardCorneringExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_HARSH_CORNERING,
        applyEventsHardCorneringAvailability: PropTypes.func.isRequired,
        timeoutEventsHardCorneringAvailability: PropTypes.func.isRequired,
        eventsHardCorneringPending: PropTypes.bool.isRequired,
        eventsHardCorneringEnabled: PropTypes.bool,
        eventsHardCorneringErrorMessage: PropTypes.string,

        applyEventsHardCorneringThreshold: PropTypes.func.isRequired,
        timeoutEventsHardCorneringThreshold: PropTypes.func.isRequired,
        eventsHardCorneringThresholdPending: PropTypes.bool.isRequired,
        eventsHardCorneringThreshold: PropTypes.number,
        eventsHardCorneringThresholdErrorMessage: PropTypes.string,

        // EventsIdling Setttings
        //eventsIdlingExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_IDLING,
        applyEventsIdlingAvailability: PropTypes.func.isRequired,
        timeoutEventsIdlingAvailability: PropTypes.func.isRequired,
        eventsIdlingPending: PropTypes.bool.isRequired,
        eventsIdlingEnabled: PropTypes.bool,
        eventsIdlingErrorMessage: PropTypes.string,

        applyEventsIdlingThreshold: PropTypes.func.isRequired,
        timeoutEventsIdlingThreshold: PropTypes.func.isRequired,
        eventsIdlingThresholdPending: PropTypes.bool.isRequired,
        eventsIdlingThreshold: PropTypes.number,
        eventsIdlingThresholdErrorMessage: PropTypes.string,

        applyEventsIdlingSpeedThreshold: PropTypes.func.isRequired,
        timeoutEventsIdlingSpeedThreshold: PropTypes.func.isRequired,
        eventsIdlingSpeedThresholdPending: PropTypes.bool.isRequired,
        eventsIdlingSpeedThreshold: PropTypes.number,
        eventsIdlingSpeedThresholdErrorMessage: PropTypes.string,

        // EventsPossibleImpact Setttings
        //eventsPossibleImpactExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_POSSIBLE_IMPACT,
        applyEventsPossibleImpactAvailability: PropTypes.func.isRequired,
        timeoutEventsPossibleImpactAvailability: PropTypes.func.isRequired,
        eventsPossibleImpactPending: PropTypes.bool.isRequired,
        eventsPossibleImpactEnabled: PropTypes.bool,
        eventsPossibleImpactErrorMessage: PropTypes.string,

        applyEventsPossibleImpactThreshold: PropTypes.func.isRequired,
        timeoutEventsPossibleImpactThreshold: PropTypes.func.isRequired,
        eventsPossibleImpactThresholdPending: PropTypes.bool.isRequired,
        eventsPossibleImpactThreshold: PropTypes.number,
        eventsPossibleImpactThresholdErrorMessage: PropTypes.string,

        // DailyFleetReport Settings
        //reportsExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.REPORTS,
        initiateAccountDailyFleetReports: PropTypes.func.isRequired,
        applyAccountDailyFleetReportAvailability: PropTypes.func.isRequired,
        timeoutAccountDailyFleetReportAvailability: PropTypes.func.isRequired,
        accountDailyFleetReportPending: PropTypes.bool.isRequired,
        accountDailyFleetReportEnabled: PropTypes.bool,
        accountDailyFleetReportErrorMessage: PropTypes.string,

        applyAccountDailyFleetReportTimezone: PropTypes.func.isRequired,
        timeoutAccountDailyFleetReportTimezone: PropTypes.func.isRequired,
        accountDailyFleetReportTimezonePending:  PropTypes.bool,
        accountDailyFleetReportTimezone: PropTypes.string,
        accountDailyFleetReportTimezoneErrorMessage: PropTypes.string,

        applyAccountDailyFleetReportEmails: PropTypes.func.isRequired,
        timeoutAccountDailyFleetReportEmails: PropTypes.func.isRequired,
        accountDailyFleetReportEmailsPending:  PropTypes.bool,
        accountDailyFleetReportEmails: PropTypes.array,
        accountDailyFleetReportEmailsErrorMessage: PropTypes.string,

        // Gestures Settings
        //gesturesExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.GESTURES,
        applyGesturesAvailability: PropTypes.func.isRequired,
        timeoutGesturesAvailability: PropTypes.func.isRequired,
        gesturesPending: PropTypes.bool.isRequired,
        gesturesEnabled: PropTypes.bool,
        gesturesErrorMessage: PropTypes.string,

        // Integrations Settings
        //integrationsExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.INTEGRATIONS,
        //integrationsFleetioCredentialsExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.INTEGRATIONS_FLEETIO,
        integrationsFleetioCredentialsPending: PropTypes.bool.isRequired,
        integrationsFleetioCredentialsAccountToken: PropTypes.string,
        integrationsFleetioCredentialsApiKey: PropTypes.string,
        integrationsFleetioCredentialsIntegrationId: PropTypes.string,
        integrationsFleetioCredentialsErrorMessage: PropTypes.string,
        integrationsFleetioCredentialsStatus: PropTypes.string,
        integrationsFleetioCredentialsStatusMessage: PropTypes.string,
        integrationsFleetioCredentialsVerificationPending: PropTypes.bool.isRequired,
        integrationsFleetioCredentialsVerificationIsValid: PropTypes.bool.isRequired,
        integrationsFleetioCredentialsVerificationErrorMessage: PropTypes.string,

        // whiparound 
        integrationsWhiparoundCredentialsPending: PropTypes.bool.isRequired,
        integrationsWhiparoundCredentialsApiKey: PropTypes.string,
        integrationsWhiparoundCredentialsIntegrationId: PropTypes.string,
        integrationsWhiparoundCredentialsErrorMessage: PropTypes.string,
        integrationsWhiparoundCredentialsStatus: PropTypes.string,
        integrationsWhiparoundCredentialsStatusMessage: PropTypes.string,
        integrationsWhiparoundCredentialsVerificationPending: PropTypes.bool.isRequired,
        integrationsWhiparoundCredentialsVerificationIsValid: PropTypes.bool.isRequired,
        integrationsWhiparoundCredentialsVerificationErrorMessage: PropTypes.string,

        // Unit Of Measure Settings
        //unitOfMeasureExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.UNIT_OF_MEASURE,
        //unitOfMeasureDistanceExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.UNIT_OF_MEASURE_DISTANCE,
        applyUnitOfMeasureDistance: PropTypes.func.isRequired,
        timeoutUnitOfMeasureDistance: PropTypes.func.isRequired,
        unitOfMeasureDistancePending: PropTypes.bool.isRequired,
        unitOfMeasureDistanceMetric: PropTypes.bool,
        unitOfMeasureDistanceErrorMessage: PropTypes.string,

        //unitOfMeasureTemperatureExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.UNIT_OF_MEASURE_TEMPERATURE,
        applyUnitOfMeasureTemperature: PropTypes.func.isRequired,
        timeoutUnitOfMeasureTemperature: PropTypes.func.isRequired,
        unitOfMeasureTemperaturePending: PropTypes.bool.isRequired,
        unitOfMeasureTemperatureMetric: PropTypes.bool,
        unitOfMeasureTemperatureErrorMessage: PropTypes.string,
    
        // Wifi Settings
        //wifiHotspotsExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.WIFI,
        applyWiFiHotspotsAvailability: PropTypes.func.isRequired,
        timeoutWiFiHotspotsAvailability: PropTypes.func.isRequired,
        wifiHotspotsPending: PropTypes.bool.isRequired,
        wifiHotspotsEnabled: PropTypes.bool,
        wifiHotspotsErrorMessage: PropTypes.string,

        //wifiHotspotsCredentialsExpandReference: SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.WIFI_CREDENTIALS,
        applyWiFiHotspotsCredentials: PropTypes.func.isRequired,
        timeoutWiFiHotspotsCredentials: PropTypes.func.isRequired,
        wifiHotspotsCredentialsPending: PropTypes.bool.isRequired,
        wifiHotspotsCredentialsSsid: PropTypes.string,
        wifiHotspotsCredentialsPassword: PropTypes.string,
        wifiHotspotsCredentialsErrorMessage: PropTypes.string
    }

    constructor(props) {

        super(props);

        this.loadSettingsTimeoutId = undefined;

        this.state = {
            confirmationDialog: null
        }

        // collapse all sections and subsections by default
        Object.values(SECTION_AND_SUBSECTION_UI_STATE_REFERENCES).forEach((uiStateReference) => {
            this.state[uiStateReference] = true;
        });
    }

    expandOrCollapseAllSectionsAndSubsections = (isExpanded) => {
        this.expandOrCollapseStateForUIReferences(isExpanded, Object.values(SECTION_AND_SUBSECTION_UI_STATE_REFERENCES));
    }

    expandOrCollapseStateForUIReferences = (isExpanded, sectionAndOrSubsectionReferences) => {
        if (!sectionAndOrSubsectionReferences || sectionAndOrSubsectionReferences.length === 0) return;

        const resultantState = {};
        Object.values(sectionAndOrSubsectionReferences).forEach((uiStateReference) => {
            resultantState[uiStateReference] = !!isExpanded;
        });
        this.setState({...resultantState});
    }

    componentDidMount() {
        this.loadSettingsTimeoutId = setTimeout(() => {this.props.timeoutLoadSettings()}, LOAD_ALL_SETTINGS_TIMEOUT);
        this.props.loadSettings(this.props.globalSettingsLoaded)
        .then(
            () => {clearTimeout(this.loadSettingsTimeoutId)},
            () => {clearTimeout(this.loadSettingsTimeoutId)}
        )
    }

    componentWillUnmount() {
        clearTimeout(this.loadSettingsTimeoutId);
    }

    showConfirmationDialog = (confirmationDialog) => {
        this.setState({
            confirmationDialog: confirmationDialog
        })
    }

    dismissConfirmationDialog = () => {
        this.setState({confirmationDialog: null})
    }

    toggleCameraAvailability(cameraType) {

        // RAV-1546 (also related to RAV-1523)
        let shouldDisableAudio = false;
        let shouldDisableGestures = false;

        switch (cameraType) {
            case "CAMERA_CABIN":
                if (this.props.cameraCabinEnabled && !this.props.cameraRoadEnabled) { // both cameras will be disabled after this operation
                    shouldDisableAudio = true;
                    shouldDisableGestures = true;                        
                }
                break;
            case "CAMERA_ROAD":
                if (this.props.cameraRoadEnabled && !this.props.cameraCabinEnabled) {
                    shouldDisableAudio = true;
                    shouldDisableGestures = true;
                }
                break;
            default:
                return Promise.reject(new Error("Invalid cameraType"));
        }

        if (shouldDisableAudio) {
             if (!this.props.cameraAudioEnabled) {
                shouldDisableAudio = false; // audio is already disabled
             }
        }
        if (shouldDisableGestures) {
            if (!this.props.gesturesEnabled) {
                shouldDisableGestures = false; // gestures are already disabled
            }
        }

        const toggleCameraCabinAvailability = () => {
            switch (cameraType) {
                case "CAMERA_CABIN": return this.props.applyCameraCabinAvailability(!this.props.cameraCabinEnabled);
                case "CAMERA_ROAD": return this.props.applyCameraRoadAvailability(!this.props.cameraRoadEnabled);
            }
        };

        const promises = [
            toggleCameraCabinAvailability()
        ];
        if (shouldDisableAudio) {
            promises.push(this.props.applyAudioAvailability(false)); //TODO UPDATE_SETTINGS_TIMEOUT is not enforced for this request
        }
        if (shouldDisableGestures) {
            promises.push(this.props.applyGesturesAvailability(false)); //TODO UPDATE_SETTINGS_TIMEOUT is not enforced for this request
        }

        return Promise.all(promises)
        .then(
           () => { return Promise.resolve(); }, // Promise.all(...).then returns values but ignoring this here
           (error) => { return Promise.reject(error); } // passed to catch below
        )
        .catch( (error) => { return Promise.reject(error);});
    }

    render() {

        const { selectedSubSection } = this.props;

        let cameraAudioSettingLockedElement = null;
        let cameraAudioSettingLocked = this.props.cameraAudioPending || this.props.globalSettingsPending; // "Locked" to avoid confusion with "Enabled/Disabled"

        let streamingVideoAudioSettingLockedElement = null;
        let streamingVideoAudioSettingLocked = this.props.cameraAudioPending || this.props.globalSettingsPending; // "Locked" to avoid confusion with "Enabled/Disabled"
        let streamingVideoAudioEnabled = this.props.streamingVideoAudioEnabled;

        if (this.props.cameraCabinEnabled === false &&
            this.props.cameraRoadEnabled === false) {
                cameraAudioSettingLockedElement = <div className="alert alert-dark setting-locked" role="alert">{strings.settings_enable_cameras_for_audio}&nbsp;{/* boostrap archor spacing workaround */}<a onClick={() => {this.expandOrCollapseStateForUIReferences(true, [SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.CAMERAS, SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.CAMERAS_VIDEO_RECORDING_PROFILE])}} href="#component_document_id_cameras">{strings.settings_global_go_to_the_section}</a></div>;
            // RAV-1546 (also related to RAV-1523):
            if (!this.props.cameraAudioEnabled) { // only prevent ENABLING (accomodates external factors and also some redundancy for missed timeouts locally)
                cameraAudioSettingLocked = true;
                //removed condition for RAV-2120: if (!this.props.streamingVideoAudioEnabled) { // only prevent ENABLING (accomodates external factors and also some redundancy for missed timeouts locally)
                streamingVideoAudioSettingLocked = true;
                //}
            }
        }

        if (this.props.cameraAudioEnabled === false) { // see also cabin+road camera check above
            streamingVideoAudioSettingLockedElement = <div className="alert alert-dark setting-locked" role="alert">{strings.settings_enable_audio_for_streaming_video_audio}</div>;
            //removed condition for RAV-2120: if (!this.props.streamingVideoAudioEnabled) { // only prevent ENABLING (accomodates external factors and also some redundancy for missed timeouts locally)
            streamingVideoAudioSettingLocked = true;
            //}
        }

        if (streamingVideoAudioSettingLocked) {
            streamingVideoAudioEnabled = false; // Only show as enabled if Audio is actually available
        }

        let videoRecordingProfileConfigAvailable = false;
        let driverAlertsSectionAvailable = true;
        let accountDailyFleetReportsSectionAvailable = false;
        let driverAlertsAudibleAudioNotificationsAvailable = true;
        let eventsSectionAvailable = false;
        let eventsSectionSpeedingConfigAvailable = false;
        let eventsSectionAggressiveAccelConfigAvailable = false;
        let eventsSectionTailgatingConfigAvailable = false;
        let eventsSectionCarBumpedConfigAvailable = false;
        let eventsSectionHardBrakingConfigAvailable = false;
        let eventsSectionHardCorneringConfigAvailable = false;
        let eventsSectionIdlingConfigAvailable = false;
        let eventsSectionPossibleImpactConfigAvailable = false;
        if (this.props.features.global_settings) {
            if (this.props.features.global_settings.daily_reports) {
                accountDailyFleetReportsSectionAvailable = true;
            }
            if (this.props.features.global_settings.video_quality) {
                videoRecordingProfileConfigAvailable = true;
            }
            if (this.props.features.global_settings.events) {
                eventsSectionAvailable = this.props.features.global_settings.events.section_available ? true : false;
                eventsSectionSpeedingConfigAvailable = this.props.features.global_settings.events.speeding ? true : false;
                eventsSectionAggressiveAccelConfigAvailable = this.props.features.global_settings.events.aggressive_accel ? true : false;
                eventsSectionTailgatingConfigAvailable = this.props.features.global_settings.events.tailgating_detection ? true : false;
                eventsSectionCarBumpedConfigAvailable = this.props.features.global_settings.events.car_bumped ? true : false;
                eventsSectionHardBrakingConfigAvailable = this.props.features.global_settings.events.hard_braking ? true : false;
                eventsSectionHardCorneringConfigAvailable = this.props.features.global_settings.events.hard_cornering ? true : false;
                eventsSectionIdlingConfigAvailable = this.props.features.global_settings.events.idling ? true : false;
                eventsSectionPossibleImpactConfigAvailable = this.props.features.global_settings.events.possible_impact ? true : false;
                
            }
            if (this.props.features.global_settings.driver_alerts) {
                driverAlertsSectionAvailable = this.props.features.global_settings.driver_alerts.section_available ? true : false;
                driverAlertsAudibleAudioNotificationsAvailable = this.props.features.global_settings.driver_alerts.audio_notifications ? true : false;
            }
        }

        let eventProps = {}
        for (let event of events){
            eventProps[`events${event.pascalName}ConfigAvailable`] = this.props.features.global_settings.events[event.snakeName] ? true : false;
            eventProps[`events${event.pascalName}ExpandReference`] = SECTION_AND_SUBSECTION_UI_STATE_REFERENCES[`EVENTS_${event.snakeName.toUpperCase()}`];
            eventProps[`events${event.pascalName}ExpandReferenceIsExpanded`] = this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES[`EVENTS_${event.snakeName.toUpperCase()}`]];
            eventProps[`events${event.pascalName}AvailabilityPending`] = this.props[`events${event.pascalName}Pending`] || this.props.globalSettingsPending;
            eventProps[`events${event.pascalName}AvailabilityEnabled`] = this.props[`events${event.pascalName}Enabled`];
            eventProps[`events${event.pascalName}AvailabilityErrorMessage`] = this.props[`events${event.pascalName}ErrorMessage`];
            eventProps[`applyEvents${event.pascalName}Availability`] = this.props[`applyEvents${event.pascalName}Availability`];
            eventProps[`timeoutEvents${event.pascalName}Availability`] = this.props[`timeoutEvents${event.pascalName}Availability`];
        }

        // Audio alert props for all events (hard-coded + generic) — for DriverAlerts
        let audioAlertProps = {}
        for (let event of ALL_EVENT_NAMES){
            audioAlertProps[`events${event.pascalName}AudioAlertPending`] = this.props[`events${event.pascalName}AudioAlertPending`] || this.props.globalSettingsPending;
            audioAlertProps[`events${event.pascalName}AudioAlert`] = this.props[`events${event.pascalName}AudioAlert`];
            audioAlertProps[`events${event.pascalName}AudioAlertErrorMessage`] = this.props[`events${event.pascalName}AudioAlertErrorMessage`];
            audioAlertProps[`applyEvents${event.pascalName}AudioAlert`] = this.props[`applyEvents${event.pascalName}AudioAlert`];
            audioAlertProps[`timeoutEvents${event.pascalName}AudioAlert`] = this.props[`timeoutEvents${event.pascalName}AudioAlert`];
        }

        // Visual alert props for all events (hard-coded + generic) — for DriverAlerts
        let visualAlertProps = {}
        for (let event of ALL_EVENT_NAMES){
            visualAlertProps[`events${event.pascalName}VisualAlertPending`] = this.props[`events${event.pascalName}VisualAlertPending`] || this.props.globalSettingsPending;
            visualAlertProps[`events${event.pascalName}VisualAlert`] = this.props[`events${event.pascalName}VisualAlert`];
            visualAlertProps[`events${event.pascalName}VisualAlertErrorMessage`] = this.props[`events${event.pascalName}VisualAlertErrorMessage`];
            visualAlertProps[`applyEvents${event.pascalName}VisualAlert`] = this.props[`applyEvents${event.pascalName}VisualAlert`];
            visualAlertProps[`timeoutEvents${event.pascalName}VisualAlert`] = this.props[`timeoutEvents${event.pascalName}VisualAlert`];
        }

        // Event availability props for all events — for DriverAlerts row locking
        let eventAvailabilityProps = {}
        for (let event of ALL_EVENT_NAMES){
            eventAvailabilityProps[`events${event.pascalName}Enabled`] = this.props[`events${event.pascalName}Enabled`];
        }

        let gesturesSettingLockedElement = null;
        let gesturesSettingLocked = this.props.gesturesPending || this.props.globalSettingsPending; // "Locked" to avoid confusion with "Enabled/Disabled"
        if (this.props.cameraCabinEnabled === false &&
            this.props.cameraRoadEnabled === false) {
            gesturesSettingLockedElement = <div className="alert alert-dark setting-locked" role="alert">{strings.settings_enable_cameras_for_gestures}&nbsp;{/* boostrap archor spacing workaround */}<a onClick={() => {this.expandOrCollapseStateForUIReferences(true, [SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.CAMERAS, SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.CAMERAS_VIDEO_RECORDING_PROFILE])}} href="#component_document_id_cameras">{strings.settings_global_go_to_the_section}</a></div>;
            // RAV-1546 (also related to RAV-1523):
            if (!this.props.gesturesEnabled) { // only prevent ENABLING (accomodates external factors and also some redundancy for missed timeouts locally)
                console.log("gesturesSettingLocked = true");
                gesturesSettingLocked = true;
            }
        }

        let videoRecordingProfileSettingLockedElement = null;
        let videoRecordingProfileSettingLocked = this.props.videoRecordingProfilePending || this.props.globalSettingsPending; // "Locked" to avoid confusion with "Enabled/Disabled"
        if (this.props.cameraCabinEnabled === false &&
            this.props.cameraRoadEnabled === false) {
            videoRecordingProfileSettingLockedElement = <div className="alert alert-dark setting-locked" role="alert">{strings.settings_enable_cameras_for_video_quality_settings}</div>;
            videoRecordingProfileSettingLocked = true;
        }

        let eventsSpeedingThreshold = this.props.eventsSpeedingThreshold;
        if (this.props.eventsSpeedingThresholdType === EVENTS_THRESHOLD_TYPES.CONSTANT) {
            if (this.props.unitOfMeasureDistanceMetric === false) {
                if (eventsSpeedingThreshold) {
                    eventsSpeedingThreshold = Number(eventsSpeedingThreshold) / 1.609; // convert to mph
                }
            }
        }
        if (eventsSpeedingThreshold) {
            eventsSpeedingThreshold = Number(eventsSpeedingThreshold.toFixed(1)); // round to one decimal place
        }
        eventsSpeedingThreshold = Math.round(eventsSpeedingThreshold); // rounding added for RAV-3502

        let eventsTailgatingDetectionSpeedThreshold = this.props.eventsTailgatingDetectionSpeedThreshold;
        if (this.props.unitOfMeasureDistanceMetric === false) {
            if (eventsTailgatingDetectionSpeedThreshold) {
                eventsTailgatingDetectionSpeedThreshold = Number(eventsTailgatingDetectionSpeedThreshold) / 1.609; // convert to mph
            }
        }
        if (eventsTailgatingDetectionSpeedThreshold) {
            eventsTailgatingDetectionSpeedThreshold = Number(eventsTailgatingDetectionSpeedThreshold.toFixed(1)); // round to one decimal place
        }
        eventsTailgatingDetectionSpeedThreshold = Math.round(eventsTailgatingDetectionSpeedThreshold); // rounding added for RAV-3502

        let eventsIdlingSpeedThreshold = this.props.eventsIdlingSpeedThreshold;
        if (this.props.unitOfMeasureDistanceMetric === false) {
            if (eventsIdlingSpeedThreshold) {
                eventsIdlingSpeedThreshold = Number(eventsIdlingSpeedThreshold) / 1.609; // convert to mph
            }
        }
        if (eventsIdlingSpeedThreshold) {
            eventsIdlingSpeedThreshold = Number(eventsIdlingSpeedThreshold.toFixed(1)); // round to one decimal place
        }
        eventsIdlingSpeedThreshold = Math.round(eventsIdlingSpeedThreshold); // rounding added for RAV-3502

        return (
            <div className="row">
                <div className="col-12">
                    <div className="error-message">
                        {this.props.globalSettingsErrorMessage}
                    </div>
                </div>
                <div className="col-12">
                    {this.props.features.global_settings ?
                        this.props.features.global_settings.audio ?
                            <div className={this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIO] ||
                                            this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIO_STREAMING] ? "settings-section" : "settings-section collapsed"}>
                                <Audio
                                        expandOrCollapseStateForUIReferences = {this.expandOrCollapseStateForUIReferences}
                                        audioExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIO}
                                        audioExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIO]}
                                        audioAvailabilityPending = {this.props.cameraAudioPending || this.props.globalSettingsPending}
                                        audioAvailabilityEnabled = {this.props.cameraAudioEnabled}
                                        audioAvailabilitySettingLocked = {cameraAudioSettingLocked}
                                        audioAvailabilitySettingLockedElement = {cameraAudioSettingLockedElement}
                                        audioAvailabilityErrorMessage = {this.props.cameraAudioErrorMessage}
                                        applyAudioAvailability = {this.props.applyAudioAvailability}
                                        timeoutAudioAvailability = {this.props.timeoutAudioAvailability}

                                        streamingVideoAudioExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIO_STREAMING}
                                        streamingVideoAudioExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIO_STREAMING]}
                                        streamingVideoAudioAvailabilityPending = {this.props.streamingVideoAudioPending || this.props.globalSettingsPending}
                                        streamingVideoAudioAvailabilityEnabled = {streamingVideoAudioEnabled}
                                        streamingVideoAudioAvailabilitySettingLocked = {streamingVideoAudioSettingLocked}
                                        streamingVideoAudioAvailabilitySettingElement = {streamingVideoAudioSettingLockedElement}
                                        streamingVideoAudioAvailabilityErrorMessage = {this.props.streamingVideoAudioErrorMessage}
                                        applyStreamingVideoAudioAvailability = {this.props.applyStreamingVideoAudioAvailability}
                                        timeoutStreamingVideoAudioAvailability = {this.props.timeoutStreamingVideoAudioAvailability}

                                        showConfirmationDialog = {this.showConfirmationDialog}
                                        dismissConfirmationDialog = {this.dismissConfirmationDialog}
                                    />
                            </div>
                        : // else (!this.props.features.global_settings.audio)
                            null // audio not available for this build environment
                    : // else (!this.props.features.global_setting)
                        <div className="error-message">
                            Build configuration error.
                        </div>
                    }

                    <div className={this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.CAMERAS] ||
                                    this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.CAMERAS_VIDEO_RECORDING_PROFILE] ? "settings-section" : "settings-section collapsed"}>
                        <Cameras
                            expandOrCollapseStateForUIReferences = {this.expandOrCollapseStateForUIReferences}
                            accountDetails = {this.props.accountDetails}
                            camerasExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.CAMERAS}
                            camerasExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.CAMERAS]}
                            cameraCabinAvailabilityPending = {this.props.cameraCabinPending || this.props.globalSettingsPending}
                            cameraCabinAvailabilityEnabled = {this.props.cameraCabinEnabled}
                            cameraCabinAvailabilityErrorMessage = {this.props.cameraCabinErrorMessage}
                            toggleCameraCabinAvailability = {() => {
                                return this.toggleCameraAvailability("CAMERA_CABIN");
                            }}
                            timeoutCameraCabinAvailability = {this.props.timeoutCameraCabinAvailability}
                            cameraRoadAvailabilityPending = {this.props.cameraRoadPending || this.props.globalSettingsPending}
                            cameraRoadAvailabilityEnabled = {this.props.cameraRoadEnabled}
                            cameraRoadAvailabilityErrorMessage = {this.props.cameraRoadErrorMessage}
                            toggleCameraRoadAvailability = {() => {
                                return this.toggleCameraAvailability("CAMERA_ROAD");
                            }}
                            timeoutCameraRoadAvailability = {this.props.timeoutCameraRoadAvailability}

                            videoRecordingProfileExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.CAMERAS_VIDEO_RECORDING_PROFILE}
                            videoRecordingProfileExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.CAMERAS_VIDEO_RECORDING_PROFILE]}
                            videoRecordingProfileConfigAvailable = {videoRecordingProfileConfigAvailable}
                            applyVideoRecordingProfile = {this.props.applyVideoRecordingProfile}
                            videoRecordingProfilePending = {this.props.videoRecordingProfilePending || this.props.globalSettingsPending}
                            videoRecordingProfile = {this.props.videoRecordingProfile}
                            videoRecordingProfileSettingLocked = {videoRecordingProfileSettingLocked}
                            videoRecordingProfileSettingLockedElement = {videoRecordingProfileSettingLockedElement}
                            videoRecordingProfileErrorMessage = {this.props.videoRecordingProfileErrorMessage}
                            timeoutVideoRecordingProfile = {this.props.timeoutVideoRecordingProfile}
                        />
                    </div>

                    {accountDailyFleetReportsSectionAvailable ?
                        <div className={this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.REPORTS] ? "settings-section" : "settings-section collapsed"}>
                            <DailyReports
                                expandOrCollapseStateForUIReferences = {this.expandOrCollapseStateForUIReferences}
                                user = {this.props.user}
                                initiateAccountDailyFleetReports = {this.props.initiateAccountDailyFleetReports}
                                reportsExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.REPORTS}
                                reportsExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.REPORTS]}
                                accountDailyFleetReportAvailabilityPending = {this.props.accountDailyFleetReportPending || this.props.globalSettingsPending}
                                accountDailyFleetReportAvailabilityEnabled = {this.props.accountDailyFleetReportEnabled}
                                accountDailyFleetReportAvailabilityErrorMessage = {this.props.accountDailyFleetReportErrorMessage}
                                applyAccountDailyFleetReportAvailability = {this.props.applyAccountDailyFleetReportAvailability}
                                timeoutAccountDailyFleetReportAvailability = {this.props.timeoutAccountDailyFleetReportAvailability}
                        
                                accountDailyFleetReportTimezonePending = {this.props.accountDailyFleetReportTimezonePending || this.props.globalSettingsPending}
                                accountDailyFleetReportTimezone = {this.props.accountDailyFleetReportTimezone}
                                accountDailyFleetReportTimezoneErrorMessage = {this.props.accountDailyFleetReportTimezoneErrorMessage}
                                applyAccountDailyFleetReportTimezone = {this.props.applyAccountDailyFleetReportTimezone}
                                timeoutAccountDailyFleetReportTimezone = {this.props.timeoutAccountDailyFleetReportTimezone}
                        
                                accountDailyFleetReportEmailsPending = {this.props.accountDailyFleetReportEmailsPending || this.props.globalSettingsPending}
                                accountDailyFleetReportEmails = {this.props.accountDailyFleetReportEmails}
                                accountDailyFleetReportEmailsErrorMessage = {this.props.accountDailyFleetReportEmailsErrorMessage}
                                applyAccountDailyFleetReportEmails = {this.props.applyAccountDailyFleetReportEmails}
                                timeoutAccountDailyFleetReportEmails = {this.props.timeoutAccountDailyFleetReportEmails}
                        
                                showConfirmationDialog = {this.showConfirmationDialog}
                                dismissConfirmationDialog = {this.dismissConfirmationDialog}
                        />
                        </div>
                    :
                        null
                    }

                    {driverAlertsSectionAvailable ?
                        <div className={this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIBLE_ALERTS] ||
                                        this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIBLE_ALERTS_AUDIO_NOTIFICATIONS] ? "settings-section" : "settings-section collapsed"}>
                            <DriverAlerts
                                enabledEvents = {this.props.features.global_settings.events}
                                expandOrCollapseStateForUIReferences = {this.expandOrCollapseStateForUIReferences}
                                driverAlertsExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIBLE_ALERTS}
                                driverAlertsExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIBLE_ALERTS]}
                                audioNotificationsExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIBLE_ALERTS_AUDIO_NOTIFICATIONS}
                                audioNotificationsExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.AUDIBLE_ALERTS_AUDIO_NOTIFICATIONS]}
                                audioNotificationsAvailabilityConfigAvailable = {driverAlertsAudibleAudioNotificationsAvailable}
                                audioNotificationsAvailabilityPending = {this.props.audioNotificationsPending || this.props.globalSettingsPending}
                                audioNotificationsAvailabilityEnabled = {this.props.audioNotificationsEnabled}
                                audioNotificationsAvailabilityErrorMessage = {this.props.audioNotificationsErrorMessage}
                                applyAudioNotificationsAvailability = {this.props.applyAudioNotificationsAvailability}
                                timeoutAudioNotificationsAvailability = {this.props.timeoutAudioNotificationsAvailability}
                                globalSettingsPending = {this.props.globalSettingsPending}
                                {...audioAlertProps}
                                {...visualAlertProps}
                                {...eventAvailabilityProps}
                                onNavigateToEventsSection = {() => {
                                    this.expandOrCollapseStateForUIReferences(true, [
                                        SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS
                                    ]);
                                }}
                            />

                        </div>
                    :
                        null
                    }

                    {eventsSectionAvailable ?
                        <div className={this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS] ||
                                        this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_SPEEDING] ||
                                        this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_AGGRESSIVE_ACCEL] ||
                                        this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_TAILGATING] ||
                                        this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_CAR_BUMPED] ||
                                        this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_HARD_BRAKING] ||
                                        this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_HARSH_CORNERING] ||
                                        this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_IDLING] ||
                                        this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_POSSIBLE_IMPACT] ? "settings-section" : "settings-section collapsed"}>
                            <Events
                                selectedSubSection = {selectedSubSection}
                                expandOrCollapseStateForUIReferences = {this.expandOrCollapseStateForUIReferences}
                                eventsExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS}
                                eventsExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS]}
                                eventsSpeedingExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_SPEEDING}
                                eventsSpeedingExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_SPEEDING]}
                                eventsSpeedingAvailabilityPending = {this.props.eventsSpeedingPending || this.props.globalSettingsPending}
                                eventsSpeedingAvailabilityEnabled = {this.props.eventsSpeedingEnabled}
                                eventsSpeedingAvailabilityErrorMessage = {this.props.eventsSpeedingErrorMessage}
                                applyEventsSpeedingAvailability = {this.props.applyEventsSpeedingAvailability}
                                timeoutEventsSpeedingAvailability = {this.props.timeoutEventsSpeedingAvailability}

                                eventsSpeedingConfigAvailable = {eventsSectionSpeedingConfigAvailable}
                                eventsSpeedingThresholdPending = {this.props.eventsSpeedingThresholdPending || this.props.globalSettingsPending}
                                eventsSpeedingThreshold = {eventsSpeedingThreshold}
                                eventsSpeedingThresholdType = {this.props.eventsSpeedingThresholdType}
                                eventsSpeedingThresholdErrorMessage = {this.props.eventsSpeedingErrorMessage}
                                applyEventsSpeedingThreshold = {this.props.applyEventsSpeedingThreshold}
                                timeoutEventsSpeedingThreshold = {this.props.timeoutEventsSpeedingAvailability}

                                unitOfMeasureDistancePending = {this.props.unitOfMeasureDistancePending || this.props.globalSettingsPending}
                                unitOfMeasureDistanceIsMetric = {this.props.unitOfMeasureDistanceMetric}
                                unitOfMeasureDistanceErrorMessage = {this.props.unitOfMeasureDistanceErrorMessage}

                                eventsAggressiveAccelExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_AGGRESSIVE_ACCEL}
                                eventsAggressiveAccelExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_AGGRESSIVE_ACCEL]}
                                eventsAggressiveAccelAvailabilityPending = {this.props.eventsAggressiveAccelPending || this.props.globalSettingsPending}
                                eventsAggressiveAccelAvailabilityEnabled = {this.props.eventsAggressiveAccelEnabled}
                                eventsAggressiveAccelAvailabilityErrorMessage = {this.props.eventsAggressiveAccelErrorMessage}
                                applyEventsAggressiveAccelAvailability = {this.props.applyEventsAggressiveAccelAvailability}
                                timeoutEventsAggressiveAccelAvailability = {this.props.timeoutEventsAggressiveAccelAvailability}

                                eventsAggressiveAccelConfigAvailable = {eventsSectionAggressiveAccelConfigAvailable}
                                eventsAggressiveAccelThresholdPending = {this.props.eventsAggressiveAccelThresholdPending || this.props.globalSettingsPending}
                                eventsAggressiveAccelThreshold = {this.props.eventsAggressiveAccelThreshold}
                                eventsAggressiveAccelThresholdErrorMessage = {this.props.eventsAggressiveAccelErrorMessage}
                                applyEventsAggressiveAccelThreshold = {this.props.applyEventsAggressiveAccelThreshold}
                                timeoutEventsAggressiveAccelThreshold = {this.props.timeoutEventsAggressiveAccelAvailability}

                                eventsTailgatingDetectionExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_TAILGATING}
                                eventsTailgatingDetectionExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_TAILGATING]}
                                eventsTailgatingDetectionAvailabilityPending = {this.props.eventsTailgatingDetectionPending || this.props.globalSettingsPending}
                                eventsTailgatingDetectionAvailabilityEnabled = {this.props.eventsTailgatingDetectionEnabled}
                                eventsTailgatingDetectionAvailabilityErrorMessage = {this.props.eventsTailgatingDetectionErrorMessage}
                                applyEventsTailgatingDetectionAvailability = {this.props.applyEventsTailgatingDetectionAvailability}
                                timeoutEventsTailgatingDetectionAvailability = {this.props.timeoutEventsTailgatingDetectionAvailability}

                                eventsTailgatingDetectionConfigAvailable = {eventsSectionTailgatingConfigAvailable}
                                eventsTailgatingDetectionSpeedThresholdPending = {this.props.eventsTailgatingDetectionSpeedThresholdPending || this.props.globalSettingsPending}
                                eventsTailgatingDetectionSpeedThreshold = {eventsTailgatingDetectionSpeedThreshold}
                                eventsTailgatingDetectionSpeedThresholdErrorMessage = {this.props.eventsTailgatingDetectionSpeedThresholdErrorMessage}
                                applyEventsTailgatingDetectionSpeedThreshold = {this.props.applyEventsTailgatingDetectionSpeedThreshold}
                                timeoutEventsTailgatingDetectionSpeedThreshold = {this.props.timeoutEventsTailgatingDetectionAvailability}

                                eventsTailgatingDetectionReactionTimeThresholdPending = {this.props.eventsTailgatingDetectionReactionTimeThresholdPending || this.props.globalSettingsPending}
                                eventsTailgatingDetectionReactionTimeThreshold = {this.props.eventsTailgatingDetectionReactionTimeThreshold}
                                eventsTailgatingDetectionReactionTimeThresholdErrorMessage = {this.props.eventsTailgatingDetectionReactionTimeThresholdErrorMessage}
                                applyEventsTailgatingDetectionReactionTimeThreshold = {this.props.applyEventsTailgatingDetectionReactionTimeThreshold}
                                timeoutEventsTailgatingDetectionReactionTimeThreshold = {this.props.timeoutEventsTailgatingDetectionAvailability}

                                eventsTailgatingDetectionFollowTimeThresholdPending = {this.props.eventsTailgatingDetectionFollowTimeThresholdPending || this.props.globalSettingsPending}
                                eventsTailgatingDetectionFollowTimeThreshold = {this.props.eventsTailgatingDetectionFollowTimeThreshold}
                                eventsTailgatingDetectionFollowTimeThresholdErrorMessage = {this.props.eventsTailgatingDetectionFollowTimeThresholdErrorMessage}
                                applyEventsTailgatingDetectionFollowTimeThreshold = {this.props.applyEventsTailgatingDetectionFollowTimeThreshold}
                                timeoutEventsTailgatingDetectionFollowTimeThreshold = {this.props.timeoutEventsTailgatingDetectionAvailability}

                                eventsCarBumpedExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_CAR_BUMPED}
                                eventsCarBumpedExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_CAR_BUMPED]}
                                eventsCarBumpedAvailabilityPending = {this.props.eventsCarBumpedPending || this.props.globalSettingsPending}
                                eventsCarBumpedAvailabilityEnabled = {this.props.eventsCarBumpedEnabled}
                                eventsCarBumpedAvailabilityErrorMessage = {this.props.eventsCarBumpedErrorMessage}
                                applyEventsCarBumpedAvailability = {this.props.applyEventsCarBumpedAvailability}
                                timeoutEventsCarBumpedAvailability = {this.props.timeoutEventsCarBumpedAvailability}

                                eventsCarBumpedConfigAvailable = {eventsSectionCarBumpedConfigAvailable}
                                eventsCarBumpedThresholdPending = {this.props.eventsCarBumpedThresholdPending || this.props.globalSettingsPending}
                                eventsCarBumpedThreshold = {this.props.eventsCarBumpedThreshold}
                                eventsCarBumpedThresholdErrorMessage = {this.props.eventsCarBumpedErrorMessage}
                                applyEventsCarBumpedThreshold = {this.props.applyEventsCarBumpedThreshold}
                                timeoutEventsCarBumpedThreshold = {this.props.timeoutEventsCarBumpedAvailability}

                                {...eventProps}               

                                eventsHardBrakingExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_HARD_BRAKING}
                                eventsHardBrakingExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_HARD_BRAKING]}
                                eventsHardBrakingAvailabilityPending = {this.props.eventsHardBrakingPending || this.props.globalSettingsPending}
                                eventsHardBrakingAvailabilityEnabled = {this.props.eventsHardBrakingEnabled}
                                eventsHardBrakingAvailabilityErrorMessage = {this.props.eventsHardBrakingErrorMessage}
                                applyEventsHardBrakingAvailability = {this.props.applyEventsHardBrakingAvailability}
                                timeoutEventsHardBrakingAvailability = {this.props.timeoutEventsHardBrakingAvailability}

                                eventsHardBrakingConfigAvailable = {eventsSectionHardBrakingConfigAvailable}
                                eventsHardBrakingThresholdPending = {this.props.eventsHardBrakingThresholdPending || this.props.globalSettingsPending}
                                eventsHardBrakingThreshold = {this.props.eventsHardBrakingThreshold}
                                eventsHardBrakingThresholdErrorMessage = {this.props.eventsHardBrakingErrorMessage}
                                applyEventsHardBrakingThreshold = {this.props.applyEventsHardBrakingThreshold}
                                timeoutEventsHardBrakingThreshold = {this.props.timeoutEventsHardBrakingAvailability}

                                eventsHardCorneringExpandReference ={SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_HARSH_CORNERING}
                                eventsHardCorneringExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_HARSH_CORNERING]}
                                eventsHardCorneringAvailabilityPending = {this.props.eventsHardCorneringPending || this.props.globalSettingsPending}
                                eventsHardCorneringAvailabilityEnabled = {this.props.eventsHardCorneringEnabled}
                                eventsHardCorneringAvailabilityErrorMessage = {this.props.eventsHardCorneringErrorMessage}
                                applyEventsHardCorneringAvailability = {this.props.applyEventsHardCorneringAvailability}
                                timeoutEventsHardCorneringAvailability = {this.props.timeoutEventsHardCorneringAvailability}

                                eventsHardCorneringConfigAvailable = {eventsSectionHardCorneringConfigAvailable}
                                eventsHardCorneringThresholdPending = {this.props.eventsHardCorneringThresholdPending || this.props.globalSettingsPending}
                                eventsHardCorneringThreshold = {this.props.eventsHardCorneringThreshold}
                                eventsHardCorneringThresholdErrorMessage = {this.props.eventsHardCorneringErrorMessage}
                                applyEventsHardCorneringThreshold = {this.props.applyEventsHardCorneringThreshold}
                                timeoutEventsHardCorneringThreshold = {this.props.timeoutEventsHardCorneringAvailability}

                                eventsIdlingExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_IDLING}
                                eventsIdlingExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_IDLING]}
                                eventsIdlingAvailabilityPending = {this.props.eventsIdlingPending || this.props.globalSettingsPending}
                                eventsIdlingAvailabilityEnabled = {this.props.eventsIdlingEnabled}
                                eventsIdlingAvailabilityErrorMessage = {this.props.eventsIdlingErrorMessage}
                                applyEventsIdlingAvailability = {this.props.applyEventsIdlingAvailability}
                                timeoutEventsIdlingAvailability = {this.props.timeoutEventsIdlingAvailability}

                                eventsIdlingConfigAvailable = {eventsSectionIdlingConfigAvailable}
                                eventsIdlingThresholdPending = {this.props.eventsIdlingThresholdPending || this.props.globalSettingsPending}
                                eventsIdlingThreshold = {this.props.eventsIdlingThreshold}
                                eventsIdlingThresholdErrorMessage = {this.props.eventsIdlingErrorMessage}
                                applyEventsIdlingThreshold = {this.props.applyEventsIdlingThreshold}
                                timeoutEventsIdlingThreshold = {this.props.timeoutEventsIdlingAvailability}

                                eventsIdlingSpeedThresholdPending = {this.props.eventsIdlingSpeedThresholdPending || this.props.globalSettingsPending}
                                eventsIdlingSpeedThreshold = {eventsIdlingSpeedThreshold}
                                eventsIdlingSpeedThresholdErrorMessage = {this.props.eventsIdlingSpeedThresholdErrorMessage}
                                applyEventsIdlingSpeedThreshold = {this.props.applyEventsIdlingSpeedThreshold}
                                timeoutEventsIdlingSpeedThreshold = {this.props.timeoutEventsIdlingSpeedThreshold}

                                eventsPossibleImpactExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_POSSIBLE_IMPACT}
                                eventsPossibleImpactExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.EVENTS_POSSIBLE_IMPACT]}
                                eventsPossibleImpactAvailabilityPending = {this.props.eventsPossibleImpactPending || this.props.globalSettingsPending}
                                eventsPossibleImpactAvailabilityEnabled = {this.props.eventsPossibleImpactEnabled}
                                eventsPossibleImpactAvailabilityErrorMessage = {this.props.eventsPossibleImpactErrorMessage}
                                applyEventsPossibleImpactAvailability = {this.props.applyEventsPossibleImpactAvailability}
                                timeoutEventsPossibleImpactAvailability = {this.props.timeoutEventsPossibleImpactAvailability}

                                eventsPossibleImpactConfigAvailable = {eventsSectionPossibleImpactConfigAvailable}
                                eventsPossibleImpactThresholdPending = {this.props.eventsPossibleImpactThresholdPending || this.props.globalSettingsPending}
                                eventsPossibleImpactThreshold = {this.props.eventsPossibleImpactThreshold}
                                eventsPossibleImpactThresholdErrorMessage = {this.props.eventsPossibleImpactErrorMessage}
                                applyEventsPossibleImpactThreshold = {this.props.applyEventsPossibleImpactThreshold}
                                timeoutEventsPossibleImpactThreshold = {this.props.timeoutEventsPossibleImpactAvailability}
                            />
                        </div>
                    :
                        null
                    }

                    {this.props.features.global_settings ?
                        this.props.features.global_settings.gestures ?
                            <div className={this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.GESTURES] ? "settings-section" : "settings-section collapsed"}>
                                <Gestures
                                    expandOrCollapseStateForUIReferences = {this.expandOrCollapseStateForUIReferences}
                                    gesturesExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.GESTURES}
                                    gesturesExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.GESTURES]}
                                    gesturesAvailabilityPending = {this.props.gesturesPending || this.props.globalSettingsPending}
                                    gesturesAvailabilityEnabled = {this.props.gesturesEnabled}
                                    gesturesAvailabilitySettingLocked = {gesturesSettingLocked}
                                    gesturesAvailabilitySettingLockedElement = {gesturesSettingLockedElement}
                                    gesturesAvailabilityErrorMessage = {this.props.gesturesErrorMessage}
                                    applyGesturesAvailability = {this.props.applyGesturesAvailability}
                                    timeoutGesturesAvailability = {this.props.timeoutGesturesAvailability}
                                />
                            </div>
                        : // else (!this.props.features.global_settings.gestures)
                            null // gestures not available for this build environment
                    : // else (!this.props.features.global_setting)
                        <div className="error-message">
                            Build configuration error.
                        </div>
                    }

                    {this.props.features.integrations ?
                        <div className={this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.INTEGRATIONS] ||
                                        this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.INTEGRATIONS_FLEETIO] ||
                                        this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.INTEGRATIONS_WHIPAROUND] ? "settings-section" : "settings-section collapsed"}>
                            <Integrations
                                expandOrCollapseStateForUIReferences = {this.expandOrCollapseStateForUIReferences}
                                integrationsExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.INTEGRATIONS}
                                integrationsExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.INTEGRATIONS]}

                                integrationsFleetioCredentialsExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.INTEGRATIONS_FLEETIO}
                                integrationsFleetioCredentialsExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.INTEGRATIONS_FLEETIO]}
                                integrationsFleetioCredentialsPending = {this.props.integrationsFleetioCredentialsPending || this.props.globalSettingsPending}
                                timeoutIntegrationsFleetioCredentials = {this.props.timeoutIntegrationsFleetioCredentials}
                                integrationsFleetioCredentialsAccountToken = {this.props.integrationsFleetioCredentialsAccountToken}
                                integrationsFleetioCredentialsApiKey = {this.props.integrationsFleetioCredentialsApiKey}
                                integrationsFleetioCredentialsIntegrationId = {this.props.integrationsFleetioCredentialsIntegrationId}
                                applyIntegrationsFleetioCredentials = {this.props.applyIntegrationsFleetioCredentials}
                                integrationsFleetioCredentialsErrorMessage = {this.props.integrationsFleetioCredentialsErrorMessage}
                                integrationsFleetioCredentialsStatus = {this.props.integrationsFleetioCredentialsStatus}
                                integrationsFleetioCredentialsStatusMessage = {this.props.integrationsFleetioCredentialsStatusMessage}
                                integrationsFleetioCredentialsVerificationPending = {this.props.integrationsFleetioCredentialsVerificationPending || this.props.globalSettingsPending}
                                integrationsFleetioCredentialsVerificationIsValid = {this.props.integrationsFleetioCredentialsVerificationIsValid}
                                integrationsFleetioCredentialsVerificationErrorMessage = {this.props.integrationsFleetioCredentialsVerificationErrorMessage}

                                integrationsWhiparoundCredentialsExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.INTEGRATIONS_WHIPAROUND}
                                integrationsWhiparoundCredentialsExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.INTEGRATIONS_WHIPAROUND]}
                                integrationsWhiparoundCredentialsPending = {this.props.integrationsWhiparoundCredentialsPending || this.props.globalSettingsPending}
                                timeoutIntegrationsWhiparoundCredentials = {this.props.timeoutIntegrationsWhiparoundCredentials}
                                integrationsWhiparoundCredentialsApiKey = {this.props.integrationsWhiparoundCredentialsApiKey}
                                integrationsWhiparoundCredentialsIntegrationId = {this.props.integrationsWhiparoundCredentialsIntegrationId}
                                applyIntegrationsWhiparoundCredentials = {this.props.applyIntegrationsWhiparoundCredentials}
                                integrationsWhiparoundCredentialsErrorMessage = {this.props.integrationsWhiparoundCredentialsErrorMessage}
                                integrationsWhiparoundCredentialsStatus = {this.props.integrationsWhiparoundCredentialsStatus}
                                integrationsWhiparoundCredentialsStatusMessage = {this.props.integrationsWhiparoundCredentialsStatusMessage}
                                integrationsWhiparoundCredentialsVerificationPending = {this.props.integrationsWhiparoundCredentialsVerificationPending || this.props.globalSettingsPending}
                                integrationsWhiparoundCredentialsVerificationIsValid = {this.props.integrationsWhiparoundCredentialsVerificationIsValid}
                                integrationsWhiparoundCredentialsVerificationErrorMessage = {this.props.integrationsWhiparoundCredentialsVerificationErrorMessage}

                                showConfirmationDialog = {this.showConfirmationDialog}
                                dismissConfirmationDialog = {this.dismissConfirmationDialog}
                            />
                        </div>
                    : // else (!this.props.features.integrations)
                        null // integrations not available for this build environment and/or account type
                    }

                    <div className={this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.UNIT_OF_MEASURE] ||
                                    this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.UNIT_OF_MEASURE_DISTANCE] ||
                                    this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.UNIT_OF_MEASURE_TEMPERATURE] ? "settings-section" : "settings-section collapsed"}>
                        <UnitsOfMeasure
                            expandOrCollapseStateForUIReferences = {this.expandOrCollapseStateForUIReferences}
                            unitOfMeasureExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.UNIT_OF_MEASURE}
                            unitOfMeasureExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.UNIT_OF_MEASURE]}
                            unitOfMeasureDistanceExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.UNIT_OF_MEASURE_DISTANCE}
                            unitOfMeasureDistanceExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.UNIT_OF_MEASURE_DISTANCE]}
                            unitOfMeasureDistancePending = {this.props.unitOfMeasureDistancePending || this.props.globalSettingsPending}
                            unitOfMeasureDistanceIsMetric = {this.props.unitOfMeasureDistanceMetric}
                            unitOfMeasureDistanceErrorMessage = {this.props.unitOfMeasureDistanceErrorMessage}
                            applyUnitOfMeasureDistance = {this.props.applyUnitOfMeasureDistance}
                            timeoutUnitOfMeasureDistance = {this.props.timeoutUnitOfMeasureDistance}
                            unitOfMeasureTemperatureExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.UNIT_OF_MEASURE_TEMPERATURE}
                            unitOfMeasureTemperatureExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.UNIT_OF_MEASURE_TEMPERATURE]}
                            unitOfMeasureTemperaturePending = {this.props.unitOfMeasureTemperaturePending || this.props.globalSettingsPending}
                            unitOfMeasureTemperatureIsMetric = {this.props.unitOfMeasureTemperatureMetric}
                            unitOfMeasureTemperatureErrorMessage = {this.props.unitOfMeasureTemperatureErrorMessage}
                            applyUnitOfMeasureTemperature = {this.props.applyUnitOfMeasureTemperature}
                            timeoutUnitOfMeasureTemperature = {this.props.timeoutUnitOfMeasureTemperature}
                        />
                    </div>

                    {this.props.features.global_settings ?
                        this.props.features.global_settings.wifi_hotspot && this.props.allRavensPlansSupportWiFi ?
                            <div className={this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.WIFI] ||
                                            this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.WIFI_CREDENTIALS] ? "settings-section" : "settings-section collapsed"}>
                                <WiFi
                                    expandOrCollapseStateForUIReferences = {this.expandOrCollapseStateForUIReferences}
                                    wifiHotspotsExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.WIFI}
                                    wifiHotspotsExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.WIFI]}
                                    wifiHotspotsAvailabilityPending = {this.props.wifiHotspotsPending || this.props.globalSettingsPending}
                                    wifiHotspotsAvailabilityEnabled = {this.props.wifiHotspotsEnabled}
                                    wifiHotspotsAvailabilityErrorMessage = {this.props.wifiHotspotsErrorMessage}
                                    applyWiFiHotspotsAvailability = {this.props.applyWiFiHotspotsAvailability}
                                    timeoutWiFiHotspotsAvailability = {this.props.timeoutWiFiHotspotsAvailability}
                                    wifiHotspotsCredentialsExpandReference = {SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.WIFI_CREDENTIALS}
                                    wifiHotspotsCredentialsExpandReferenceIsExpanded = {this.state[SECTION_AND_SUBSECTION_UI_STATE_REFERENCES.WIFI_CREDENTIALS]}
                                    wifiHotspotsCredentialsPending = {this.props.wifiHotspotsCredentialsPending || this.props.globalSettingsPending}
                                    wifiHotspotsCredentialsSsid = {this.props.wifiHotspotsCredentialsSsid}
                                    wifiHotspotsCredentialsPassword = {this.props.wifiHotspotsCredentialsPassword}
                                    wifiHotspotsCredentialsErrorMessage = {this.props.wifiHotspotsCredentialsErrorMessage}
                                    applyWiFiHotspotsCredentials = {this.props.applyWiFiHotspotsCredentials}
                                    timeoutWiFiHotspotsCredentials = {this.props.timeoutWiFiHotspotsCredentials}
                                />
                            </div>
                        : // else (!this.props.features.global_settings.wifi_hotspot)
                            null // wifi_hotspot not available for this build environment and/or account type
                    : // else (!this.props.features.global_setting)
                        <div className="error-message">
                            Build configuration error.
                        </div>
                    }
                </div>
                {this.state.confirmationDialog}
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    if (!state.data.globalSettings) {
        console.error("GlobalSettings mapStateToProps globalSettings reducer property not found");
        return {};
    }

    const { globalSettings } = state.data;

    return {
        ...globalSettings // see data/globalSettings' globalSettingsInitialState for guide
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    return {

        loadSettings: (isLoading) => {
            dispatch(dispatchActions.updatingGlobalSettings(isLoading));
            return RcAPI.refreshTempCredentials(ownProps.user.stage)
            .then(
                () => {
                    return GlobalSettingsAPI.getAccountSettings(ownProps.user.stage, dispatch)
                    .then(
                        () => { //(response) => {
                            dispatch(dispatchActions.setGlobalSettings(true));
                            return Promise.resolve();
                        },
                        (error) => { return Promise.reject(error);} // passed to .catch below
                    )
                    .catch(
                        (error) => {
                            console.error(error);
                            return Promise.reject(error); // passed to parent .error below
                        }
                    )
                },
                (error) =>  {
                    return Promise.reject(error); // passed to .catch below
                }
            )
            .catch( (error) => {
                // error refreshing credentials
                dispatch(dispatchActions.setGlobalSettings(false, strings.settings_error_load_all_request_error));
                // might as well set all value errors, also:
                dispatch(dispatchActions.setAudioAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setStreamingVideoAudioAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setAudioNotificationsAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setCameraCabinAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setCameraRoadAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setVideoRecordingProfile(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsSpeedingAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsSpeedingThreshold(undefined, undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsAggressiveAccelAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsAggressiveAccelThreshold(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsTailgatingDetectionAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsTailgatingDetectionSpeedThreshold(undefined, undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsTailgatingDetectionReactionTimeThreshold(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsTailgatingDetectionFollowTimeThreshold(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsCarBumpedAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsCarBumpedThreshold(undefined, strings.settings_error_request_error));
                for(let event in events){
                  dispatch(dispatchActions[`setEvents${event.pascalName}Availability`](undefined, strings.settings_error_request_error));
                }
                dispatch(dispatchActions.setEventsHardBrakingAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsHardBrakingThreshold(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsHardCorneringAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsHardCorneringThreshold(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsIdlingAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsIdlingThreshold(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsIdlingSpeedThreshold(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsPossibleImpactAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setEventsPossibleImpactThreshold(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setAccountDailyFleetReportAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setAccountDailyFleetReportTimezone(undefined, undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setAccountDailyFleetReportEmails(undefined, undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setGesturesAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setIntegrationsFleetioCredentials(undefined, undefined, undefined, undefined, undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setIntegrationsWhipAroundCredentials(undefined, undefined, undefined, undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setUnitOfMeasureDistance(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setUnitOfMeasureTemperature(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setWiFiHotspotAvailability(undefined, strings.settings_error_request_error));
                dispatch(dispatchActions.setWiFiHotspotCredentials(undefined, undefined, strings.settings_error_request_error));
                console.error(error);
                return Promise.reject();
            });
        },
        timeoutLoadSettings: () => {
            // error refreshing credentials
            dispatch(dispatchActions.setGlobalSettings(false, strings.settings_error_load_all_request_timeout));
            // might as well set all value errors, also:
            dispatch(dispatchActions.setAudioAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setStreamingVideoAudioAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setAudioNotificationsAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setCameraCabinAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setCameraRoadAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setVideoRecordingProfile(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsSpeedingAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsSpeedingThreshold(undefined, undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsAggressiveAccelAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsAggressiveAccelThreshold(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsTailgatingDetectionAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsTailgatingDetectionSpeedThreshold(undefined, undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsTailgatingDetectionReactionTimeThreshold(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsTailgatingDetectionFollowTimeThreshold(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsCarBumpedAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsCarBumpedThreshold(undefined, strings.settings_error_request_timeout));
            for(let event in events){
              dispatch(dispatchActions[`setEvents${event.pascalName}Availability`](undefined, strings.settings_error_request_timeout));
            }
            dispatch(dispatchActions.setEventsHardBrakingAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsHardBrakingThreshold(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsHardCorneringAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsHardCorneringThreshold(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsIdlingAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsIdlingThreshold(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsIdlingSpeedThreshold(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsPossibleImpactAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setEventsPossibleImpactThreshold(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setAccountDailyFleetReportAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setAccountDailyFleetReportTimezone(undefined, undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setAccountDailyFleetReportEmails(undefined, undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setGesturesAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setIntegrationsFleetioCredentials(undefined, undefined, undefined, undefined, undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setIntegrationsWhipAroundCredentials(undefined, undefined, undefined, undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setUnitOfMeasureDistance(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setUnitOfMeasureTemperature(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setWiFiHotspotAvailability(undefined, strings.settings_error_request_timeout));
            dispatch(dispatchActions.setWiFiHotspotCredentials(undefined, undefined, strings.settings_error_request_timeout));
        },

        // Audio settings change handlers
        applyAudioAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingAudioAvailability(isEnabled));
            return GlobalSettingsAPI.setAudio(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setAudioAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setAudioAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutAudioAvailability: (isEnabled) => {
            dispatch(dispatchActions.setAudioAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        applyStreamingVideoAudioAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingStreamingVideoAudioAvailability(isEnabled));
            return GlobalSettingsAPI.setStreamingVideoAudio(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setStreamingVideoAudioAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => {
                    return Promise.reject(error);
                }
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setStreamingVideoAudioAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutStreamingVideoAudioAvailability: (isEnabled) => {
            dispatch(dispatchActions.setStreamingVideoAudioAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        applyAudioNotificationsAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingAudioNotificationsAvailability(isEnabled));
            return GlobalSettingsAPI.setAudioNotifications(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setAudioNotificationsAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setAudioNotificationsAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutAudioNotificationsAvailability: (isEnabled) => {
            dispatch(dispatchActions.setAudioNotificationsAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        // Camera settings change handlers
        applyCameraCabinAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingCameraCabinAvailability(isEnabled));
            return GlobalSettingsAPI.setCameraCabin(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setCameraCabinAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setCameraCabinAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutCameraCabinAvailability: (isEnabled) => {
            dispatch(dispatchActions.setCameraCabinAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        applyCameraRoadAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingCameraRoadAvailability(isEnabled));
            return GlobalSettingsAPI.setCameraRoad(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setCameraRoadAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setCameraRoadAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutCameraRoadAvailability: (isEnabled) => {
            dispatch(dispatchActions.setCameraRoadAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        applyVideoRecordingProfile: (videoRecordingProfile, oldVideoRecordingProfile) => {
            dispatch(dispatchActions.updatingVideoRecordingProfile(videoRecordingProfile));
            return GlobalSettingsAPI.setVideoRecordingProfile(videoRecordingProfile, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setVideoRecordingProfile(videoRecordingProfile));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setVideoRecordingProfile(oldVideoRecordingProfile, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutVideoRecordingProfile: (oldVideoRecordingProfile) => {
            dispatch(dispatchActions.setVideoRecordingProfile(oldVideoRecordingProfile, strings.settings_error_request_timeout));
        },

        // EventsSpeeding settings change handlers
        applyEventsSpeedingAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingEventsSpeedingAvailability(isEnabled));
            return GlobalSettingsAPI.setEventsSpeeding(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsSpeedingAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsSpeedingAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsSpeedingAvailability: (isEnabled) => {
            dispatch(dispatchActions.setEventsSpeedingAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        applyEventsSpeedingThreshold: (threshold, thresholdType, oldThreshold, oldThresholdType) => {
            dispatch(dispatchActions.updatingEventsSpeedingThreshold(threshold, thresholdType));
            return GlobalSettingsAPI.setEventsSpeedingThreshold(threshold, thresholdType, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsSpeedingThreshold(threshold, thresholdType));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsSpeedingThreshold(oldThreshold, oldThresholdType, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsSpeedingThreshold: (oldThreshold, oldThresholdType) => {
            dispatch(dispatchActions.setEventsSpeedingThreshold(oldThreshold, oldThresholdType, strings.settings_error_request_timeout));
        },

        // EventsAggressiveAccel settings change handlers
        applyEventsAggressiveAccelAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingEventsAggressiveAccelAvailability(isEnabled));
            return GlobalSettingsAPI.setEventsAggressiveAccel(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsAggressiveAccelAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsAggressiveAccelAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsAggressiveAccelAvailability: (isEnabled) => {
            dispatch(dispatchActions.setEventsAggressiveAccelAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        applyEventsAggressiveAccelThreshold: (threshold, oldThreshold) => {
            dispatch(dispatchActions.updatingEventsAggressiveAccelThreshold(threshold));
            return GlobalSettingsAPI.setEventsAggressiveAccelThreshold(threshold, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsAggressiveAccelThreshold(threshold));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsAggressiveAccelThreshold(oldThreshold, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsAggressiveAccelThreshold: (oldThreshold) => {
            dispatch(dispatchActions.setEventsAggressiveAccelThreshold(oldThreshold, strings.settings_error_request_timeout));
        },

        // EventsTailgatingDetection settings change handlers
        applyEventsTailgatingDetectionAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingEventsTailgatingDetectionAvailability(isEnabled));
            return GlobalSettingsAPI.setEventsTailgatingDetection(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsTailgatingDetectionAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsTailgatingDetectionAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsTailgatingDetectionAvailability: (isEnabled) => {
            dispatch(dispatchActions.setEventsTailgatingDetectionAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        applyEventsTailgatingDetectionSpeedThreshold: (threshold, oldThreshold) => {
            console.log("applyEventsTailgatingDetectionSpeedThreshold", threshold, oldThreshold);
            dispatch(dispatchActions.updatingEventsTailgatingDetectionSpeedThreshold(threshold));
            return GlobalSettingsAPI.setEventsTailgatingDetectionSpeedThreshold(threshold, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsTailgatingDetectionSpeedThreshold(threshold));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsTailgatingDetectionSpeedThreshold(oldThreshold, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsTailgatingDetectionSpeedThreshold: (oldThreshold) => {
            dispatch(dispatchActions.setEventsTailgatingDetectionSpeedThreshold(oldThreshold, strings.settings_error_request_timeout));
        },

        applyEventsTailgatingDetectionReactionTimeThreshold: (threshold, oldThreshold) => {
            console.log("applyEventsTailgatingDetectionReactionTimeThreshold", threshold, oldThreshold);
            dispatch(dispatchActions.updatingEventsTailgatingDetectionReactionTimeThreshold(threshold));
            return GlobalSettingsAPI.setEventsTailgatingDetectionReactionTimeThreshold(threshold, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsTailgatingDetectionReactionTimeThreshold(threshold));
                    return Promise.resolve();
                }
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsTailgatingDetectionReactionTimeThreshold(oldThreshold, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsTailgatingDetectionReactionTimeThreshold: (oldThreshold) => {
            dispatch(dispatchActions.setEventsTailgatingDetectionReactionTimeThreshold(oldThreshold, strings.settings_error_request_timeout));
        },
        
        applyEventsTailgatingDetectionFollowTimeThreshold: (threshold, oldThreshold) => {
            dispatch(dispatchActions.updatingEventsTailgatingDetectionFollowTimeThreshold(threshold));
            return GlobalSettingsAPI.setEventsTailgatingDetectionFollowTimeThreshold(threshold, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsTailgatingDetectionFollowTimeThreshold(threshold));
                    return Promise.resolve();
                }
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsTailgatingDetectionFollowTimeThreshold(oldThreshold, strings.settings_error_request_error));
                return Promise.reject();
            });
        },

        // EventsCarBumped settings change handlers
        applyEventsCarBumpedAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingEventsCarBumpedAvailability(isEnabled));
            return GlobalSettingsAPI.setEventsCarBumped(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsCarBumpedAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsCarBumpedAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsCarBumpedAvailability: (isEnabled) => {
            dispatch(dispatchActions.setEventsCarBumpedAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        applyEventsCarBumpedThreshold: (threshold, oldThreshold) => {
            dispatch(dispatchActions.updatingEventsCarBumpedThreshold(threshold));
            return GlobalSettingsAPI.setEventsCarBumpedThreshold(threshold, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsCarBumpedThreshold(threshold));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsCarBumpedThreshold(oldThreshold, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsCarBumpedThreshold: (oldThreshold) => {
            dispatch(dispatchActions.setEventsCarBumpedThreshold(oldThreshold, strings.settings_error_request_timeout));
        },

        ...events.reduce((acc, event) => {
          acc[`applyEvents${event.pascalName}Availability`] = (isEnabled) => {
              dispatch(dispatchActions[`updatingEvents${event.pascalName}Availability`](isEnabled));
              return GlobalSettingsAPI[`setEvents${event.pascalName}`](isEnabled, ownProps.user.stage)
              .then(
                  () => {
                      dispatch(dispatchActions[`setEvents${event.pascalName}Availability`](isEnabled));
                      return Promise.resolve();
                  },
                  (error) => { return Promise.reject(error);}
              )
              .catch( (error) => {
                  console.error(error);
                  dispatch(dispatchActions[`setEvents${event.pascalName}Availability`](!isEnabled, strings.settings_error_request_error));
                  return Promise.reject();
              });
          };
          acc[`timeoutEvents${event.pascalName}Availability`] = (isEnabled) => {
              dispatch(dispatchActions[`setEvents${event.pascalName}Availability`](isEnabled, strings.settings_error_request_timeout));
          };

          return acc;
        }, {}),

        // Audio alert change handlers for all events (hard-coded + generic)
        ...ALL_EVENT_NAMES.reduce((acc, event) => {
          acc[`applyEvents${event.pascalName}AudioAlert`] = (audioAlertType, oldAudioAlertType) => {
              dispatch(dispatchActions[`updatingEvents${event.pascalName}AudioAlert`](audioAlertType));
              return GlobalSettingsAPI[`setEvents${event.pascalName}AudioAlert`](audioAlertType, ownProps.user.stage)
              .then(
                  () => {
                      dispatch(dispatchActions[`setEvents${event.pascalName}AudioAlert`](audioAlertType));
                      return Promise.resolve();
                  },
                  (error) => { return Promise.reject(error);}
              )
              .catch( (error) => {
                  console.error(error);
                  dispatch(dispatchActions[`setEvents${event.pascalName}AudioAlert`](oldAudioAlertType, strings.settings_error_request_error));
                  return Promise.reject();
              });
          };
          acc[`timeoutEvents${event.pascalName}AudioAlert`] = (oldValue) => {
              dispatch(dispatchActions[`setEvents${event.pascalName}AudioAlert`](oldValue, strings.settings_error_request_timeout));
          };

          return acc;
        }, {}),

        // Visual alert change handlers for all events (hard-coded + generic)
        // When disabling visual alert and current audio_alert is BEEP, also reset audio_alert to NONE in the same PATCH request
        ...ALL_EVENT_NAMES.reduce((acc, event) => {
          acc[`applyEvents${event.pascalName}VisualAlert`] = (isEnabled, oldIsEnabled, currentAudioAlert) => {
              const shouldResetAudio = isEnabled === false && currentAudioAlert === AUDIO_ALERT_TYPES.BEEP;
              dispatch(dispatchActions[`updatingEvents${event.pascalName}VisualAlert`](isEnabled));
              if (shouldResetAudio) {
                  dispatch(dispatchActions[`updatingEvents${event.pascalName}AudioAlert`](AUDIO_ALERT_TYPES.NONE));
              }
              return GlobalSettingsAPI[`setEvents${event.pascalName}VisualAlert`](isEnabled, ownProps.user.stage, currentAudioAlert)
              .then(
                  () => {
                      dispatch(dispatchActions[`setEvents${event.pascalName}VisualAlert`](isEnabled));
                      if (shouldResetAudio) {
                          dispatch(dispatchActions[`setEvents${event.pascalName}AudioAlert`](AUDIO_ALERT_TYPES.NONE));
                      }
                      return Promise.resolve();
                  },
                  (error) => { return Promise.reject(error);}
              )
              .catch( (error) => {
                  console.error(error);
                  dispatch(dispatchActions[`setEvents${event.pascalName}VisualAlert`](oldIsEnabled, strings.settings_error_request_error));
                  if (shouldResetAudio) {
                      dispatch(dispatchActions[`setEvents${event.pascalName}AudioAlert`](currentAudioAlert, strings.settings_error_request_error));
                  }
                  return Promise.reject();
              });
          };
          acc[`timeoutEvents${event.pascalName}VisualAlert`] = (oldValue, oldAudioAlert) => {
              dispatch(dispatchActions[`setEvents${event.pascalName}VisualAlert`](oldValue, strings.settings_error_request_timeout));
              if (oldAudioAlert) {
                  dispatch(dispatchActions[`setEvents${event.pascalName}AudioAlert`](oldAudioAlert, strings.settings_error_request_timeout));
              }
          };

          return acc;
        }, {}),

        // EventsHardBraking settings change handlers
        applyEventsHardBrakingAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingEventsHardBrakingAvailability(isEnabled));
            return GlobalSettingsAPI.setEventsHardBraking(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsHardBrakingAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsHardBrakingAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsHardBrakingAvailability: (isEnabled) => {
            dispatch(dispatchActions.setEventsHardBrakingAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        applyEventsHardBrakingThreshold: (threshold, oldThreshold) => {
            dispatch(dispatchActions.updatingEventsHardBrakingThreshold(threshold));
            return GlobalSettingsAPI.setEventsHardBrakingThreshold(threshold, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsHardBrakingThreshold(threshold));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsHardBrakingThreshold(oldThreshold, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsHardBrakingThreshold: (oldThreshold) => {
            dispatch(dispatchActions.setEventsHardBrakingThreshold(oldThreshold, strings.settings_error_request_timeout));
        },

        // EventsHardCornering settings change handlers
        applyEventsHardCorneringAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingEventsHardCorneringAvailability(isEnabled));
            return GlobalSettingsAPI.setEventsHardCornering(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsHardCorneringAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsHardCorneringAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsHardCorneringAvailability: (isEnabled) => {
            dispatch(dispatchActions.setEventsHardCorneringAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        applyEventsHardCorneringThreshold: (threshold, oldThreshold) => {
            dispatch(dispatchActions.updatingEventsHardCorneringThreshold(threshold));
            return GlobalSettingsAPI.setEventsHardCorneringThreshold(threshold, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsHardCorneringThreshold(threshold));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsHardCorneringThreshold(oldThreshold, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsHardCorneringThreshold: (oldThreshold) => {
            dispatch(dispatchActions.setEventsHardCorneringThreshold(oldThreshold, strings.settings_error_request_timeout));
        },

        // EventsIdling settings change handlers
        applyEventsIdlingAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingEventsIdlingAvailability(isEnabled));
            return GlobalSettingsAPI.setEventsIdling(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsIdlingAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsIdlingAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsIdlingAvailability: (isEnabled) => {
            dispatch(dispatchActions.setEventsIdlingAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        applyEventsIdlingThreshold: (threshold, oldThreshold) => {
            dispatch(dispatchActions.updatingEventsIdlingThreshold(threshold));
            return GlobalSettingsAPI.setEventsIdlingThreshold(threshold, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsIdlingThreshold(threshold));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsIdlingThreshold(oldThreshold, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsIdlingThreshold: (oldThreshold) => {
            dispatch(dispatchActions.setEventsIdlingThreshold(oldThreshold, strings.settings_error_request_timeout));
        },

        applyEventsIdlingSpeedThreshold: (threshold, oldThreshold) => {
            dispatch(dispatchActions.updatingEventsIdlingSpeedThreshold(threshold));
            return GlobalSettingsAPI.setEventsIdlingSpeedThreshold(threshold, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsIdlingSpeedThreshold(threshold));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                dispatch(dispatchActions.setEventsIdlingSpeedThreshold(oldThreshold, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsIdlingSpeedThreshold: (oldThreshold) => {
            dispatch(dispatchActions.setEventsIdlingSpeedThreshold(oldThreshold, strings.settings_error_request_timeout));
        },


        // EventsPossibleImpact settings change handlers
        applyEventsPossibleImpactAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingEventsPossibleImpactAvailability(isEnabled));
            return GlobalSettingsAPI.setEventsPossibleImpact(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsPossibleImpactAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsPossibleImpactAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsPossibleImpactAvailability: (isEnabled) => {
            dispatch(dispatchActions.setEventsPossibleImpactAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        applyEventsPossibleImpactThreshold: (threshold, oldThreshold) => {
            dispatch(dispatchActions.updatingEventsPossibleImpactThreshold(threshold));
            return GlobalSettingsAPI.setEventsPossibleImpactThreshold(threshold, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setEventsPossibleImpactThreshold(threshold));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setEventsPossibleImpactThreshold(oldThreshold, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutEventsPossibleImpactThreshold: (oldThreshold) => {
            dispatch(dispatchActions.setEventsPossibleImpactThreshold(oldThreshold, strings.settings_error_request_timeout));
        },

        // AccountDailyFleetReport settings change handlers
        applyAccountDailyFleetReportAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingAccountDailyFleetReportAvailability(isEnabled));
            return GlobalSettingsAPI.setAccountDailyFleetReport(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setAccountDailyFleetReportAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setAccountDailyFleetReportAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutAccountDailyFleetReportAvailability: (isEnabled) => {
            dispatch(dispatchActions.setAccountDailyFleetReportAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        applyAccountDailyFleetReportTimezone: (timezone, oldTimezone) => {
            dispatch(dispatchActions.updatingAccountDailyFleetReportTimezone(timezone));
            return GlobalSettingsAPI.setAccountDailyFleetReportTimezone(timezone, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setAccountDailyFleetReportTimezone(timezone));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setAccountDailyFleetReportTimezone(oldTimezone, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutAccountDailyFleetReportTimezone: (oldTimezone) => {
            dispatch(dispatchActions.setAccountDailyFleetReportTimezone(oldTimezone, strings.settings_error_request_timeout));
        },

        applyAccountDailyFleetReportEmails: (emailsArray, oldEmailsArray) => {
            dispatch(dispatchActions.updatingAccountDailyFleetReportEmails(emailsArray));
            return GlobalSettingsAPI.setAccountDailyFleetReportEmails(emailsArray, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setAccountDailyFleetReportEmails(emailsArray));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setAccountDailyFleetReportEmails(oldEmailsArray, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutAccountDailyFleetReportEmails: (oldEmailsArray) => {
            dispatch(dispatchActions.setAccountDailyFleetReportEmails(oldEmailsArray, strings.settings_error_request_timeout));
        },

        initiateAccountDailyFleetReports: (dailyFleetReportsSettings, oldDailyFleetReportsSettings) => {
            dispatch(dispatchActions.updatingAccountDailyFleetReportAvailability(dailyFleetReportsSettings.enabled));
            dispatch(dispatchActions.updatingAccountDailyFleetReportTimezone(dailyFleetReportsSettings.timezone));
            dispatch(dispatchActions.updatingAccountDailyFleetReportEmails(dailyFleetReportsSettings.emails));
            return GlobalSettingsAPI.setAccountDailyFleetReports(dailyFleetReportsSettings, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setAccountDailyFleetReportAvailability(dailyFleetReportsSettings.enabled));
                    dispatch(dispatchActions.setAccountDailyFleetReportTimezone(dailyFleetReportsSettings.timezone));
                    dispatch(dispatchActions.setAccountDailyFleetReportEmails(dailyFleetReportsSettings.emails));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setAccountDailyFleetReportAvailability(oldDailyFleetReportsSettings.enabled, strings.settings_error_request_error));
                dispatch(dispatchActions.setAccountDailyFleetReportTimezone(oldDailyFleetReportsSettings.timezone, strings.settings_error_request_error));
                dispatch(dispatchActions.setAccountDailyFleetReportEmails(oldDailyFleetReportsSettings.emails, strings.settings_error_request_error));
                return Promise.reject();
            });
        },

        // Gestures settings change handlers
        applyGesturesAvailability: (isEnabled) => {
            console.log("applyGesturesAvailability 1555", isEnabled);
            dispatch(dispatchActions.updatingGesturesAvailability(isEnabled));
            return GlobalSettingsAPI.setGestures(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setGesturesAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setGesturesAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutGesturesAvailability: (isEnabled) => {
            console.log("timeoutGesturesAvailability 1571", isEnabled);
            dispatch(dispatchActions.setGesturesAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        // Integrations settings change handlers
        // Fleetio
        applyIntegrationsFleetioCredentials: (accountToken, apiKey, integrationId = undefined) => {
            dispatch(dispatchActions.updatingIntegrationsFleetioCredentials(accountToken, apiKey, integrationId));
            return GlobalSettingsAPI.setIntegrationsCredentials(INTEGRATIONS_TYPES_NAMES.FLEETIO, accountToken, apiKey, integrationId, ownProps.user.stage)
            .then(
                (newIntegrationGetResponse) => {
                    if (!integrationId) { // applyIntegrationsFleetioCredentials without an integrationId is a POST to add new; otherwise it's a DELETE
                        const integrationIdForNewCreds = newIntegrationGetResponse.fleetio ? newIntegrationGetResponse.fleetio.integrationId : undefined;
                        if (!integrationIdForNewCreds) {
                            dispatch(dispatchActions.setIntegrationsFleetioCredentials(accountToken, apiKey, integrationId, strings.settings_error_request_error));
                        } else {
                            dispatch(dispatchActions.setIntegrationsFleetioCredentials(accountToken, apiKey, integrationIdForNewCreds));
                        }
                        return Promise.resolve();
                    }
                    dispatch(dispatchActions.setIntegrationsFleetioCredentials(accountToken, apiKey, integrationId));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setIntegrationsFleetioCredentials(accountToken, apiKey, integrationId, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutIntegrationsFleetioCredentials: (accountToken, apiKey, integrationId = undefined) => {
            dispatch(dispatchActions.setIntegrationsFleetioCredentials(accountToken, apiKey, integrationId, strings.settings_error_request_timeout));
        },

        verifyIntegrationsFleetioCredentials: (accountToken, apiKey) => { // Roughed in feature to be completed when "test" endpoint is available
            dispatch(dispatchActions.verifyingIntegrationsFleetioCredentials(accountToken, apiKey));
            return GlobalSettingsAPI.verifyIntegrationsCredentials(INTEGRATIONS_TYPES_NAMES.FLEETIO, accountToken, apiKey, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.verifyIntegrationsFleetioCredentials(accountToken, apiKey));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.verifyIntegrationsFleetioCredentials(accountToken, apiKey, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutVerifyIntegrationsFleetioCredentials: (accountToken, apiKey) => {
            dispatch(dispatchActions.setIntegrationsFleetioCredentials(accountToken, apiKey, strings.settings_error_request_timeout));
        },

        // Whiparound
        applyIntegrationsWhiparoundCredentials: (apiKey, integrationId = undefined) => {
            dispatch(dispatchActions.updatingIntegrationsWhiparoundCredentials(apiKey, integrationId));
            return GlobalSettingsAPI.setIntegrationsCredentials(INTEGRATIONS_TYPES_NAMES.WHIPAROUND, undefined, apiKey, integrationId, ownProps.user.stage)
            .then(
                (newIntegrationGetResponse) => {
                    if (!integrationId) { // applyIntegrationsWhiparoundCredentials without an integrationId is a POST to add new; otherwise it's a DELETE
                        const integrationIdForNewCreds = newIntegrationGetResponse.whiparound ? newIntegrationGetResponse.whiparound.integrationId : undefined;
                        if (!integrationIdForNewCreds) {
                            dispatch(dispatchActions.setIntegrationsWhiparoundCredentials(apiKey, integrationId, strings.settings_error_request_error));
                        } else {
                            dispatch(dispatchActions.setIntegrationsWhiparoundCredentials(apiKey, integrationIdForNewCreds));
                        }
                        return Promise.resolve();
                    }
                    dispatch(dispatchActions.setIntegrationsWhiparoundCredentials(apiKey, integrationId));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setIntegrationsWhiparoundCredentials(apiKey, integrationId, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutIntegrationsWhiparoundCredentials: (apiKey, integrationId = undefined) => {
            dispatch(dispatchActions.setIntegrationsWhiparoundCredentials(apiKey, integrationId, strings.settings_error_request_timeout));
        },

        verifyIntegrationsWhiparoundCredentials: (apiKey) => { // Roughed in feature to be completed when "test" endpoint is available
            dispatch(dispatchActions.verifyIntegrationsWhiparoundCredentials(apiKey));
            return GlobalSettingsAPI.verifyIntegrationsCredentials(INTEGRATIONS_TYPES_NAMES.WHIPAROUND, undefined, apiKey, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.verifyIntegrationsWhiparoundCredentials(apiKey));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.verifyIntegrationsWhiparoundCredentials(apiKey, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutVerifyIntegrationsWhiparoundCredentials: (apiKey) => {
            dispatch(dispatchActions.setIntegrationsWhiparoundCredentials(apiKey, strings.settings_error_request_timeout));
        },

        // Unit of Measure settings change handlers
        applyUnitOfMeasureDistance: (isMetric) => {
            dispatch(dispatchActions.updatingUnitOfMeasureDistance(isMetric));
            return GlobalSettingsAPI.setUnitOfMeasureDistance(isMetric, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setUnitOfMeasureDistance(isMetric));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setUnitOfMeasureDistance(!isMetric, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutUnitOfMeasureDistance: (isEnabled) => {
            dispatch(dispatchActions.setUnitOfMeasureDistance(isEnabled, strings.settings_error_request_timeout));
        },

        applyUnitOfMeasureTemperature: (isMetric) => {
            dispatch(dispatchActions.updatingUnitOfMeasureTemperature(isMetric));
            return GlobalSettingsAPI.setUnitOfMeasureTemperature(isMetric, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setUnitOfMeasureTemperature(isMetric));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setUnitOfMeasureTemperature(!isMetric, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutUnitOfMeasureTemperature: (isEnabled) => {
            dispatch(dispatchActions.setUnitOfMeasureTemperature(isEnabled, strings.settings_error_request_timeout));
        },
        
        // WiFi settings change handlers
        applyWiFiHotspotsAvailability: (isEnabled) => {
            dispatch(dispatchActions.updatingWiFiHotspotAvailability(isEnabled));
            return GlobalSettingsAPI.setWiFiHotspots(isEnabled, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setWiFiHotspotAvailability(isEnabled));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setWiFiHotspotAvailability(!isEnabled, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutWiFiHotspotsAvailability: (isEnabled) => {
            dispatch(dispatchActions.setWiFiHotspotAvailability(isEnabled, strings.settings_error_request_timeout));
        },

        applyWiFiHotspotsCredentials: (ssid, password, oldSsid, oldPassword) => {
            dispatch(dispatchActions.updatingWiFiHotspotCredentials(ssid, password));
            return GlobalSettingsAPI.setWiFiHotspotsCredentials(ssid, password, ownProps.user.stage)
            .then(
                () => {
                    dispatch(dispatchActions.setWiFiHotspotCredentials(ssid, password));
                    return Promise.resolve();
                },
                (error) => { return Promise.reject(error);}
            )
            .catch( (error) => {
                console.error(error);
                dispatch(dispatchActions.setWiFiHotspotCredentials(oldSsid, oldPassword, strings.settings_error_request_error));
                return Promise.reject();
            });
        },
        timeoutWiFiHotspotsCredentials: (oldSsid, oldPassword) => {
            dispatch(dispatchActions.setWiFiHotspotCredentials(oldSsid, oldPassword, strings.settings_error_request_timeout));
        },

    };
};

export default connect(mapStateToProps, mapDispatchToProps)(GlobalSection);
